import {FC, KeyboardEvent, useEffect, useRef, useState} from "react";
import {useHistory} from "react-router-dom";
import {Send24Filled, Sparkle24Filled} from "@fluentui/react-icons";
import {useBentoIntelligence} from "../../BentoIntelligenceContext";
import {getAgentIcon} from "../../insightAgents";
import type {AgentIdentity} from "../../insightAgents";
import type {
    ActionWorkflowDefinition,
    WorkflowEditableField,
    WorkflowStep
} from "./workflowModels";
import {modifyWorkflow} from "./workflowModifyAPI";
import {generateActionPlan} from "./actionPlanAPI";
import {generateDecisionContext} from "./decisionSupportContextAPI";
import type {DecisionSupportContext} from "./decisionSupport";
import {clearWorkflow, saveWorkflow} from "./workflowStore";
import insightActionsStore from "@/features/coachInsights/api/insightActionsStore";
import {InsightActionStatus} from "@/features/coachInsights/api/coachInsightsModels";
import {
    DECISION_SUPPORT_BY_ACTION_ID,
    formatCandidateOption,
    groupByFromReportCandidate,
    parseCandidateFromOption
} from "./decisionSupport";
import type {DecisionCandidate, DecisionSupportConfig} from "./decisionSupport";
import {DecisionCandidateCards} from "./DecisionCandidateCards";
import {DecisionSupportContextPanel} from "./DecisionSupportContextPanel";
import {ProjectedImpactPanel} from "./ProjectedImpactPanel";

const WORKFLOW_ACCENT = "#8c7bff";

// ── Chat model ───────────────────────────────────────────────────────────────

interface FieldUpdate {
    id: string;
    label: string;
    newValue: string;
}

interface WorkflowChatMessage {
    id: string;
    role: "user" | "assistant";
    text: string;
    fieldUpdates?: FieldUpdate[];
}

// ── Editable field component ─────────────────────────────────────────────────

interface FieldProps {
    field: WorkflowEditableField;
    highlighted: boolean;
    onChange: (id: string, newValue: string) => void;
}

const WfField: FC<FieldProps> = ({field, highlighted, onChange}) => {
    const selectedOptions = field.value
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);

    return (
        <div className={`pi-wf-field${highlighted ? " pi-wf-field--updated" : ""}`}>
            <label className="pi-wf-field__label">{field.label}</label>
            {field.type === "select" && field.options ? (
                <select
                    className="pi-wf-field__input pi-wf-field__input--select"
                    value={field.value}
                    onChange={(e) => onChange(field.id, e.target.value)}
                >
                    {field.options.map((opt) => (
                        <option key={opt} value={opt}>
                            {opt}
                        </option>
                    ))}
                </select>
            ) : field.type === "multiselect" && field.options ? (
                <div className="pi-wf-field__checkboxes">
                    {field.options.map((opt) => (
                        <label key={opt} className="pi-wf-field__checkbox-option">
                            <input
                                type="checkbox"
                                checked={selectedOptions.includes(opt)}
                                onChange={(e) => {
                                    const next = e.target.checked
                                        ? [...selectedOptions, opt]
                                        : selectedOptions.filter((v) => v !== opt);
                                    onChange(field.id, next.join(", "));
                                }}
                            />
                            {opt}
                        </label>
                    ))}
                </div>
            ) : (
                <input
                    className="pi-wf-field__input"
                    type={
                        field.type === "number"
                            ? "number"
                            : field.type === "date"
                              ? "date"
                              : "text"
                    }
                    value={field.value}
                    onChange={(e) => onChange(field.id, e.target.value)}
                />
            )}
            {highlighted && (
                <span className="pi-wf-field__updated-badge">Updated by AI</span>
            )}
            {field.hint && <p className="pi-wf-field__hint">{field.hint}</p>}
        </div>
    );
};

// ── Chat message renderer ────────────────────────────────────────────────────

const ChatMessage: FC<{message: WorkflowChatMessage; agent?: AgentIdentity}> = ({
    message,
    agent
}) => {
    // Render **bold** markdown in assistant replies
    const renderText = (text: string) => {
        const parts = text.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) =>
            part.startsWith("**") ? (
                <strong key={i}>{part.slice(2, -2)}</strong>
            ) : (
                part
            )
        );
    };
    const AgentIcon = agent ? getAgentIcon(agent) : undefined;

    return (
        <div className={`pi-wf-chat-msg pi-wf-chat-msg--${message.role}`}>
            {message.role === "assistant" && (
                <span
                    className={`pi-wf-chat-msg__avatar${
                        agent ? " pi-wf-chat-msg__avatar--agent" : ""
                    }`}
                    style={
                        agent
                            ? ({"--par-agent": WORKFLOW_ACCENT} as React.CSSProperties)
                            : undefined
                    }
                >
                    {AgentIcon ? <AgentIcon /> : <Sparkle24Filled />}
                </span>
            )}
            <div className="pi-wf-chat-msg__bubble">{renderText(message.text)}</div>
        </div>
    );
};

// ── Success state ────────────────────────────────────────────────────────────

interface SuccessViewProps {
    definition: ActionWorkflowDefinition;
    onClose: () => void;
}

const SuccessView: FC<SuccessViewProps> = ({definition, onClose}) => (
    <div className="pi-workflow__success-body">
        <div className="pi-workflow__success-icon">
            <svg viewBox="0 0 24 24" fill="none">
                <circle
                    cx="12"
                    cy="12"
                    r="11"
                    stroke="currentColor"
                    strokeWidth="2"
                />
                <path
                    d="M7.5 12.5L10.5 15.5L16.5 9"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
        <h3 className="pi-workflow__success-title">Workflow launched</h3>
        <p className="pi-workflow__success-msg">{definition.successMessage}</p>
        <button
            className="pi-btn pi-btn--ghost pi-workflow__success-close"
            onClick={onClose}
        >
            Back to chat
        </button>
    </div>
);

// ── Main workflow composer ───────────────────────────────────────────────────

interface Props {
    definition: ActionWorkflowDefinition;
    businessUnitId: string;
    onBack: () => void;
}


export const IntelligenceWorkflowView: FC<Props> = ({
    definition,
    businessUnitId,
    onBack
}) => {
    // Always start from the action's defaults — never hydrate a prior LLM draft
    // from localStorage. Each open of Update Segment / Update Offer / etc.
    // should get a fresh plan; localStorage is only for handoff to the campaign
    // editor (and similar) after the user launches.
    const [steps, setSteps] = useState<WorkflowStep[]>(definition.steps);
    const [fields, setFields] = useState<WorkflowEditableField[]>(
        definition.editableFields
    );
    const [intent, setIntent] = useState<string>(definition.intent);
    const [planStatus, setPlanStatus] = useState<"idle" | "generating">("idle");
    const [launched, setLaunched] = useState(false);
    const [chatMessages, setChatMessages] = useState<WorkflowChatMessage[]>([]);
    const [chatInput, setChatInput] = useState("");
    const [isThinking, setIsThinking] = useState(false);
    const [highlightedFieldIds, setHighlightedFieldIds] = useState<Set<string>>(
        new Set()
    );

    // Decision-support candidates (gift type swap, win-back offer, review
    // routing, audit/schedule scope) — only populated when this workflow's
    // action is registered in decisionSupport.ts.
    const decisionConfig: DecisionSupportConfig | undefined = definition.actionId
        ? DECISION_SUPPORT_BY_ACTION_ID[definition.actionId]
        : undefined;
    const [candidates, setCandidates] = useState<DecisionCandidate[] | null>(null);
    const [decisionContext, setDecisionContext] = useState<DecisionSupportContext | null>(
        null
    );
    const [decisionContextLoading, setDecisionContextLoading] = useState(false);
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | null>(
        null
    );
    const [candidateJustUpdated, setCandidateJustUpdated] = useState(false);
    const candidatesRef = useRef<DecisionCandidate[] | null>(null);
    const selectedCandidateIdRef = useRef<string | null>(null);

    const threadRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const agent = definition.agent;
    const AgentIcon = agent
        ? getAgentIcon(agent, definition.sourceInsightId)
        : undefined;
    const accent = WORKFLOW_ACCENT;
    const history = useHistory();
    const {closeDrawer} = useBentoIntelligence();

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    useEffect(() => {
        candidatesRef.current = candidates;
    }, [candidates]);

    useEffect(() => {
        selectedCandidateIdRef.current = selectedCandidateId;
    }, [selectedCandidateId]);

    // Replaces (or appends) the decision-support select field in a fields array
    // with one reflecting the current candidate list + selection, so it survives
    // both the LLM action-plan generation and the reset/chat flows below without
    // losing its options.
    const decorateFields = (
        baseFields: WorkflowEditableField[],
        config: DecisionSupportConfig,
        candidateList: DecisionCandidate[] | null,
        selectedId: string | null
    ): WorkflowEditableField[] => {
        const withoutDecisionField = baseFields.filter((f) => f.id !== config.fieldId);
        const selected =
            candidateList?.find((c) => c.id === selectedId) ?? candidateList?.[0];
        return [
            ...withoutDecisionField,
            {
                id: config.fieldId,
                label: config.fieldLabel,
                type: "select" as const,
                value: selected ? formatCandidateOption(selected) : "",
                options: candidateList ? candidateList.map(formatCandidateOption) : []
            }
        ];
    };

    // LLM-composed Insight / Action / Goal framing, with static fallback.
    useEffect(() => {
        if (!decisionConfig) {
            setDecisionContext(null);
            setDecisionContextLoading(false);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        setDecisionContext(null);
        setDecisionContextLoading(true);

        generateDecisionContext(
            {
                insightTitle: definition.sourceInsightTitle,
                narrative: definition.sourceInsightNarrative,
                aiNote: definition.sourceInsightAiNote,
                headlineMetricValue: definition.sourceHeadlineMetricValue,
                headlineMetricLabel: definition.sourceHeadlineMetricLabel,
                impactSummary: definition.impactSummary,
                actionTitle: definition.title,
                actionDescription: definition.actionDescription,
                actionCategory: definition.category
            },
            controller.signal
        )
            .then((context) => {
                if (!cancelled) {
                    setDecisionContext(context);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setDecisionContext(decisionConfig.fallbackContext);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setDecisionContextLoading(false);
                }
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definition.id]);

    // Load decision-support candidates from the local registry (mock evidence +
    // real gift type names where available).
    useEffect(() => {
        if (!decisionConfig) {
            setCandidates(null);
            setSelectedCandidateId(null);
            return;
        }

        let cancelled = false;
        const controller = new AbortController();
        setCandidates(null);
        setSelectedCandidateId(null);

        decisionConfig
            .loadCandidates(controller.signal)
            .then((list) => {
                if (cancelled) return;
                const recommended = list.find((c) => c.recommended) ?? list[0];
                setCandidates(list);
                setSelectedCandidateId(recommended?.id ?? null);
                setFields((prev) =>
                    decorateFields(prev, decisionConfig, list, recommended?.id ?? null)
                );
            })
            .catch(() => {
                // Candidate evidence is non-critical — leave the section empty.
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definition.id]);

    // Compose the plan with the LLM on every open so the steps + summary fit the
    // specific insight and action the user chose (delete vs edit vs create read
    // very differently). Persist the result for campaign-editor handoff, but do
    // not reuse a prior draft when the user re-clicks the action CTA.
    useEffect(() => {
        let cancelled = false;
        const controller = new AbortController();
        clearWorkflow(definition.id);
        setSteps(definition.steps);
        setFields(definition.editableFields);
        setIntent(definition.intent);
        setChatMessages([]);
        setPlanStatus("generating");

        generateActionPlan(
            {
                insightTitle: definition.sourceInsightTitle ?? definition.title,
                insightSummary:
                    definition.sourceInsightNarrative ??
                    definition.intent,
                actionTitle: definition.title,
                actionCategory: definition.category,
                actionKind: definition.kind,
                campaignAction: definition.campaignAction,
                impactSummary: definition.impactSummary,
                fields: definition.editableFields.map((f) => ({
                    label: f.label,
                    value: f.value
                }))
            },
            controller.signal
        )
            .then((plan) => {
                if (cancelled || plan.steps.length === 0) {
                    return;
                }
                const nextSteps: WorkflowStep[] = plan.steps.map((s, i) => ({
                    id: `${i + 1}`,
                    label: s.label,
                    detail: s.detail
                }));
                const nextFields: WorkflowEditableField[] =
                    plan.fields && plan.fields.length > 0
                        ? plan.fields.map((f, i) => ({
                              id: `field_${i}`,
                              label: f.label,
                              type: "text" as const,
                              value: f.value,
                              hint: f.hint
                          }))
                        : definition.editableFields;
                const decoratedFields = decisionConfig
                    ? decorateFields(
                          nextFields,
                          decisionConfig,
                          candidatesRef.current,
                          selectedCandidateIdRef.current
                      )
                    : nextFields;
                setSteps(nextSteps);
                setIntent(plan.intent);
                setFields(decoratedFields);
                saveWorkflow({
                    ...definition,
                    intent: plan.intent,
                    steps: nextSteps,
                    editableFields: decoratedFields
                });
            })
            .catch(() => {
                // Keep the fallback steps if generation fails.
            })
            .finally(() => {
                if (!cancelled) {
                    setPlanStatus("idle");
                }
            });

        return () => {
            cancelled = true;
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [definition.id]);

    const buildCurrentWorkflow = (): ActionWorkflowDefinition => ({
        ...definition,
        intent,
        steps,
        editableFields: fields
    });

    // Scroll chat thread to bottom when new messages arrive
    useEffect(() => {
        if (threadRef.current) {
            threadRef.current.scrollTop = threadRef.current.scrollHeight;
        }
    }, [chatMessages, isThinking]);

    const handleFieldChange = (id: string, newValue: string) => {
        setFields((prev) =>
            prev.map((f) => (f.id === id ? {...f, value: newValue} : f))
        );
    };

    const handleCandidateSelect = (candidateId: string) => {
        setSelectedCandidateId(candidateId);
        if (decisionConfig) {
            setFields((prev) =>
                decorateFields(prev, decisionConfig, candidates, candidateId)
            );
        }
    };

    const handleReset = () => {
        setSteps(definition.steps);
        setIntent(definition.intent);
        const recommended =
            candidates?.find((c) => c.recommended) ?? candidates?.[0] ?? null;
        setSelectedCandidateId(recommended?.id ?? null);
        setFields(
            decisionConfig
                ? decorateFields(
                      definition.editableFields,
                      decisionConfig,
                      candidates,
                      recommended?.id ?? null
                  )
                : definition.editableFields
        );
        clearWorkflow(definition.id);
    };

    const handleSend = async () => {
        const trimmed = chatInput.trim();
        if (!trimmed || isThinking || !businessUnitId) return;

        const userMsg: WorkflowChatMessage = {
            id: `u_${Date.now()}`,
            role: "user",
            text: trimmed
        };
        const nextMessages = [...chatMessages, userMsg];
        setChatMessages(nextMessages);
        setChatInput("");
        setIsThinking(true);

        abortControllerRef.current?.abort();
        const controller = new AbortController();
        abortControllerRef.current = controller;

        try {
            const response = await modifyWorkflow({
                userMessage: trimmed,
                workflow: buildCurrentWorkflow(),
                conversationHistory: nextMessages.slice(0, -1).map((message) => ({
                    role: message.role,
                    content: message.text
                })),
                businessUnitId,
                signal: controller.signal
            });

            if (controller.signal.aborted) {
                return;
            }

            if (response.fieldUpdates.length > 0) {
                const updatedIds = new Set(
                    response.fieldUpdates.map((update) => update.id)
                );
                setHighlightedFieldIds(updatedIds);
                window.setTimeout(() => setHighlightedFieldIds(new Set()), 2200);
            }

            setSteps(response.updatedWorkflow.steps);
            setFields(response.updatedWorkflow.editableFields);
            setIntent(response.updatedWorkflow.intent);

            if (decisionConfig) {
                const updatedField = response.updatedWorkflow.editableFields.find(
                    (f) => f.id === decisionConfig.fieldId
                );
                const matchedCandidate = updatedField
                    ? parseCandidateFromOption(
                          updatedField.value,
                          candidatesRef.current ?? []
                      )
                    : undefined;
                if (matchedCandidate) {
                    setSelectedCandidateId(matchedCandidate.id);
                    setCandidateJustUpdated(true);
                    window.setTimeout(() => setCandidateJustUpdated(false), 2200);
                }
            }

            const assistantMsg: WorkflowChatMessage = {
                id: `a_${Date.now()}`,
                role: "assistant",
                text: response.assistantMessage,
                fieldUpdates: response.fieldUpdates
            };
            setChatMessages((prev) => [...prev, assistantMsg]);
        } catch (error) {
            if (controller.signal.aborted) {
                return;
            }

            const assistantMsg: WorkflowChatMessage = {
                id: `a_${Date.now()}`,
                role: "assistant",
                text:
                    error instanceof Error
                        ? `Sorry, I couldn't apply that change: ${error.message}`
                        : "Sorry, I couldn't apply that change. Please try again."
            };
            setChatMessages((prev) => [...prev, assistantMsg]);
        } finally {
            if (!controller.signal.aborted) {
                setIsThinking(false);
            }
        }
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    // Campaign actions (delete / retarget / swap gift type / create) leave the
    // drawer and confirm the change on the campaign-editor page; checklist
    // actions leave the drawer and open the actual checklist page, labor
    // actions leave the drawer and open the labor-schedule page, and the void
    // & refund review action leaves the drawer and opens the trend report
    // page, once the user has previewed/iterated the plan here; every other
    // workflow shows the in-drawer success state.
    const markInProgress = () => {
        if (definition.sourceInsightId) {
            void insightActionsStore.setStatus(
                definition.sourceInsightId,
                InsightActionStatus.InProgress
            );
        }
    };

    const handleLaunch = () => {
        const selectedCandidate = candidates?.find(
            (c) => c.id === selectedCandidateId
        );
        launch(buildCurrentWorkflow(), selectedCandidate);
    };

    const handleSkip = () => {
        launch(definition);
    };

    const launch = (
        workflow: ActionWorkflowDefinition,
        selectedCandidate?: DecisionCandidate
    ) => {
        if (definition.campaignAction) {
            saveWorkflow(workflow);
            markInProgress();
            closeDrawer();
            history.push("/par-intelligence/campaign-editor", {
                workflowId: definition.id,
                campaignAction: definition.campaignAction,
                title: definition.title,
                sourceInsightTitle: definition.sourceInsightTitle,
                sourceInsightId: definition.sourceInsightId,
                campaignId: definition.campaignId,
                selectedCandidate: selectedCandidate
                    ? {
                          redeemableUuid: selectedCandidate.redeemableUuid,
                          name: selectedCandidate.name
                      }
                    : undefined
            });
            return;
        }
        if (definition.kind === "checklist") {
            saveWorkflow(workflow);
            markInProgress();
            closeDrawer();
            history.push("/par-intelligence/checklist", {
                insightKey: definition.sourceInsightId,
                workflowId: definition.id
            });
            return;
        }
        if (definition.opensLaborSchedule) {
            saveWorkflow(workflow);
            markInProgress();
            closeDrawer();
            history.push("/par-intelligence/labor-schedule", {
                insightKey: definition.sourceInsightId,
                sourceInsightTitle: definition.sourceInsightTitle
            });
            return;
        }
        if (definition.opensVoidRefundReport) {
            saveWorkflow(workflow);
            markInProgress();
            closeDrawer();
            const groupByOverride = groupByFromReportCandidate(selectedCandidate?.id);
            history.push("/par-intelligence/void-refund-report", {
                insightKey: definition.sourceInsightId,
                sourceInsightTitle: definition.sourceInsightTitle,
                reportContext: definition.reportContext
                    ? {
                          ...definition.reportContext,
                          ...(groupByOverride ? {groupBy: groupByOverride} : {})
                      }
                    : definition.reportContext
            });
            return;
        }
        markInProgress();
        setLaunched(true);
    };

    if (launched) {
        return <SuccessView definition={definition} onClose={onBack} />;
    }

    return (
        <div className="pi-workflow">
            {/* Sub-header */}
            <div className="pi-workflow__subheader">
                <button
                    className="pi-workflow__back-btn"
                    onClick={onBack}
                    aria-label="Back to chat"
                >
                    <svg viewBox="0 0 16 16" fill="none">
                        <path
                            d="M10 12L6 8L10 4"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    Back to chat
                </button>
                <span
                    className="pi-workflow__kind-chip"
                    style={{
                        background: `color-mix(in srgb, ${accent} 14%, white)`,
                        color: accent,
                        borderColor: `color-mix(in srgb, ${accent} 30%, white)`
                    }}
                >
                    {definition.category}
                </span>
            </div>

            {decisionConfig && (
                <div className="pi-workflow__skip-row">
                    <button
                        type="button"
                        className="pi-workflow__skip-link"
                        onClick={handleSkip}
                    >
                        {decisionConfig.skipLabel}
                    </button>
                </div>
            )}

            {/* Scrollable content area */}
            <div className="pi-workflow__scroll">
                {/* Title + source */}
                <div className="pi-workflow__heading">
                    {agent && (
                        <div
                            className="pi-workflow__agent"
                            style={{"--par-agent": accent} as React.CSSProperties}
                        >
                            <span className="pi-workflow__agent-avatar">
                                {AgentIcon ? <AgentIcon /> : <Sparkle24Filled />}
                            </span>
                            <span className="pi-workflow__agent-text">
                                <strong>{agent.name}</strong> is setting this up for
                                you
                            </span>
                        </div>
                    )}
                    <h2 className="pi-workflow__title">{definition.title}</h2>
                    {definition.sourceInsightTitle && (
                        <p className="pi-workflow__source">
                            From: {definition.sourceInsightTitle}
                        </p>
                    )}
                </div>

                {decisionConfig && (
                    <section className="pi-workflow__section pi-workflow__section--context">
                        {decisionContextLoading && (
                            <div
                                className="pi-workflow__context-skeleton"
                                aria-label="Loading insight context"
                            />
                        )}
                        {!decisionContextLoading && decisionContext && (
                            <DecisionSupportContextPanel context={decisionContext} />
                        )}
                    </section>
                )}

                {/* Overview section */}
                <section className="pi-workflow__section">
                    <h3 className="pi-workflow__section-title">What happens</h3>
                    <p className="pi-workflow__intent">{intent}</p>
                    {!decisionConfig && definition.impactSummary && (
                        <div className="pi-workflow__impact">
                            <span
                                className="pi-workflow__impact-dot"
                                style={{background: accent}}
                            />
                            {definition.impactSummary}
                        </div>
                    )}
                </section>

                {decisionConfig && (
                    <section className="pi-workflow__section">
                        <h3 className="pi-workflow__section-title">
                            {decisionConfig.cardsSectionTitle}
                        </h3>
                        <DecisionCandidateCards
                            candidates={candidates}
                            selectedId={selectedCandidateId}
                            onSelect={handleCandidateSelect}
                            justUpdatedByChat={candidateJustUpdated}
                        />
                        <ProjectedImpactPanel
                            candidate={
                                candidates?.find((c) => c.id === selectedCandidateId) ??
                                null
                            }
                            accent={accent}
                        />
                    </section>
                )}

                {/* Editable fields */}
                {planStatus !== "generating" && fields.length > 0 && (
                    <section className="pi-workflow__section">
                        <h3 className="pi-workflow__section-title">Customize</h3>
                        <div className="pi-workflow__fields">
                            {fields
                                .filter((field) => field.id !== decisionConfig?.fieldId)
                                .map((field) => (
                                    <WfField
                                        key={field.id}
                                        field={field}
                                        highlighted={highlightedFieldIds.has(field.id)}
                                        onChange={handleFieldChange}
                                    />
                                ))}
                        </div>
                        <button
                            className="pi-workflow__reset-btn"
                            onClick={handleReset}
                        >
                            Reset to defaults
                        </button>
                    </section>
                )}
            </div>

            {/* Chat thread — shown once conversation starts */}
            {(chatMessages.length > 0 || isThinking) && (
                <div className="pi-workflow__chat-thread" ref={threadRef}>
                    {chatMessages.map((msg) => (
                        <ChatMessage key={msg.id} message={msg} agent={agent} />
                    ))}
                    {isThinking && (
                        <div className="pi-wf-chat-msg pi-wf-chat-msg--assistant">
                            <span
                                className={`pi-wf-chat-msg__avatar${
                                    agent ? " pi-wf-chat-msg__avatar--agent" : ""
                                }`}
                                style={
                                    agent
                                        ? ({
                                                                                            "--par-agent": WORKFLOW_ACCENT
                                          } as React.CSSProperties)
                                        : undefined
                                }
                            >
                                {AgentIcon ? (
                                    <AgentIcon />
                                ) : (
                                    <Sparkle24Filled />
                                )}
                            </span>
                            <div className="pi-wf-chat-msg__bubble">
                                <span className="pi-thinking">
                                    <span className="pi-thinking__dot" />
                                    <span className="pi-thinking__dot" />
                                    <span className="pi-thinking__dot" />
                                </span>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Chat input bar */}
            <div className="pi-workflow__chat-bar">
                <div className="pi-workflow__chat-field">
                    <textarea
                        className="pi-workflow__chat-textarea"
                        rows={1}
                        placeholder="Ask a question or request a change…"
                        value={chatInput}
                        disabled={isThinking}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <button
                        type="button"
                        className="pi-workflow__chat-send"
                        aria-label="Send"
                        disabled={!chatInput.trim() || isThinking}
                        onClick={handleSend}
                    >
                        <Send24Filled />
                    </button>
                </div>
            </div>

            {/* Sticky launch footer */}
            <div className="pi-workflow__footer">
                <button
                    className="pi-workflow__launch-btn"
                    style={{background: accent}}
                    onClick={handleLaunch}
                >
                    {definition.ctaLabel}
                </button>
            </div>
        </div>
    );
};

export default IntelligenceWorkflowView;
